import { Reference } from '@apollo/client'
import { ReadFieldFunction } from '@apollo/client/cache/core/types/common'

import { SortOrder } from '~generated/types'
import { isEmpty, sortObject } from '~lib/helpers'

export type Filter<F extends Record<string, any>> = (ref: Reference, filter: F, readField: ReadFieldFunction) => boolean

export type FilterMap<T> = Partial<Record<keyof T, Filter<T>>>

export function getCacheKey(filters: Record<string, any>) {
  return JSON.stringify(sortObject({ ...filters } || {}))
}

export function getFilters<T>(filter: Record<string, any> = {}, filterFnMap: FilterMap<T>) {
  return Object.keys(filter)
    .map((key) => filterFnMap[key])
    .filter(Boolean)
}

type GetFilteredDataParams = {
  filter: Record<string, any>
  data: any[]
  filters: ReturnType<typeof getFilters>
  readField: ReadFieldFunction
}

export function getFilteredData({ filter, data, filters, readField }: GetFilteredDataParams) {
  return isEmpty(filter) ? data : data.filter((doc) => filters.every((filterFn) => filterFn(doc, filter, readField)))
}

export type SortParams = {
  data: Reference[]
  order: SortOrder
  readField: ReadFieldFunction
}

type GetSortedDataParams = {
  data: Reference[]
  sortKey: string
  sortOrder: SortOrder
  sortings: Record<string, (params: SortParams) => Reference[]>
  readField: ReadFieldFunction
}

export function getSortedData({ data, sortKey, sortOrder, sortings, readField }: GetSortedDataParams) {
  const sort = sortings[sortKey]
  return sort ? sort({ data, order: sortOrder, readField }) : data
}

type GetPaginatedDataParams = {
  filteredData: ReturnType<typeof getFilteredData>
  page: number
  limit: number
}

export function getPaginatedData({ filteredData, page, limit }: GetPaginatedDataParams) {
  return filteredData.slice(0, (page || 1) * (limit || 10))
}

type GetNewIncomingDataParams = {
  key?: string
  incomingData: Array<Reference>
  existingData: Array<Reference>
  readField: ReadFieldFunction
}

export function getNewIncomingData({ key = 'id', incomingData, existingData, readField }: GetNewIncomingDataParams) {
  return [...incomingData].filter((docA) => {
    return !existingData.some((docB) => readField(key, docA) === readField(key, docB))
  })
}
